import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { getWhatsappTicketFlowUsage } from "../../api/client";

const SKILL_LABEL = {
  ticket_seller: "removeTicketFlowSkillSale",
  ticket_preseller: "removeTicketFlowSkillPresale",
};

function UsageRow({ title, detail, impact, muted }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 1,
        bgcolor: muted ? "action.hover" : "background.paper",
        border: "1px solid",
        borderColor: muted ? "divider" : "warning.light",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {detail ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
          {detail}
        </Typography>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
        {impact}
      </Typography>
    </Box>
  );
}

export default function TicketFlowRemoveDialog({ open, channelId, removing, onClose, onConfirm }) {
  const { t } = useTranslation("settings");
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!open || !channelId) return undefined;
    let cancelled = false;
    setUsage(null);
    setLoadError(false);
    setLoading(true);
    getWhatsappTicketFlowUsage(channelId)
      .then((data) => {
        if (!cancelled) setUsage(data || {});
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, channelId]);

  const skillLabel = (key) => t(`channelForm.waCloudUi.${SKILL_LABEL[key] || "removeTicketFlowSkillSale"}`);
  const rows = [];
  const agent = usage?.channelAgent;
  if (agent) {
    const skills = Array.isArray(agent.skills) ? agent.skills : [];
    if (!skills.length) {
      rows.push({
        key: "agent-empty",
        muted: true,
        title: agent.name,
        detail: t("channelForm.waCloudUi.removeTicketFlowAgentHere"),
        impact: t("channelForm.waCloudUi.removeTicketFlowAgentNoSkillImpact"),
      });
    }
    skills.forEach((skill) => {
      const label = skillLabel(skill.key);
      rows.push({
        key: `agent-${skill.key}`,
        muted: !skill.offersFlow,
        title: agent.name,
        detail: t("channelForm.waCloudUi.removeTicketFlowAgentSkill", { skill: label }),
        impact: skill.offersFlow
          ? t("channelForm.waCloudUi.removeTicketFlowAgentOnImpact")
          : t("channelForm.waCloudUi.removeTicketFlowAgentOffImpact", { skill: label }),
      });
    });
  } else if (usage) {
    rows.push({
      key: "no-agent",
      muted: true,
      title: t("channelForm.waCloudUi.removeTicketFlowNoAgent"),
      impact: t("channelForm.waCloudUi.removeTicketFlowNoAgentImpact"),
    });
  }

  (usage?.otherAgents || []).forEach((other) => {
    const skills = (other.skills || []).map(skillLabel).join(", ");
    rows.push({
      key: `other-${other.id}`,
      title: other.name,
      detail: skills,
      impact: t("channelForm.waCloudUi.removeTicketFlowOtherAgentImpact"),
    });
  });

  (usage?.chatbotSteps || []).forEach((step) => {
    const flow = step.flowName
      ? t("channelForm.waCloudUi.removeTicketFlowStepFlow", { flowName: step.flowName })
      : "";
    rows.push({
      key: `step-${step.id}`,
      title: t("channelForm.waCloudUi.removeTicketFlowStepTitle", { name: step.name }),
      detail: `${step.chatbotName || t("channelForm.waCloudUi.removeTicketFlowChatbot")}${flow}`,
      impact: t("channelForm.waCloudUi.removeTicketFlowStepImpact"),
    });
  });

  const openSessions = Number(usage?.openSessions) || 0;
  if (openSessions > 0) {
    rows.push({
      key: "sessions",
      title: t(
        openSessions === 1
          ? "channelForm.waCloudUi.removeTicketFlowSessionsOne"
          : "channelForm.waCloudUi.removeTicketFlowSessionsMany",
        { count: openSessions }
      ),
      impact: t("channelForm.waCloudUi.removeTicketFlowSessionsImpact"),
    });
  }

  const offersSomething =
    (agent?.skills || []).some((skill) => skill.offersFlow) ||
    (usage?.otherAgents || []).length > 0 ||
    (usage?.chatbotSteps || []).length > 0;

  return (
    <Dialog open={open} onClose={removing ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("channelForm.waCloudUi.removeTicketFlowTitle")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary">
          {t("channelForm.waCloudUi.removeTicketFlowIntro")}
        </Typography>
        {loading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t("channelForm.waCloudUi.removeTicketFlowLoadingUsage")}
            </Typography>
          </Stack>
        ) : null}
        {loadError ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t("channelForm.waCloudUi.removeTicketFlowUsageError")}
          </Alert>
        ) : null}
        {!loading && usage ? (
          <Stack spacing={1.25} sx={{ mt: 2 }}>
            <Typography variant="overline" color="text.secondary">
              {t("channelForm.waCloudUi.removeTicketFlowUsageHeading")}
            </Typography>
            {!offersSomething ? (
              <Alert severity="info">
                {t("channelForm.waCloudUi.removeTicketFlowUnused")}
              </Alert>
            ) : null}
            {rows.map((row) => (
              <UsageRow key={row.key} {...row} />
            ))}
            <UsageRow
              muted
              title={t("channelForm.waCloudUi.removeTicketFlowUnpublishTitle")}
              impact={t("channelForm.waCloudUi.removeTicketFlowUnpublishImpact")}
            />
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={removing}>
          {t("channelForm.waCloudUi.removeTicketFlowCancel")}
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={onConfirm}
          disabled={loading || removing}
        >
          {removing
            ? t("channelForm.waCloudUi.removeTicketFlowLoading")
            : t("channelForm.waCloudUi.removeTicketFlowConfirmButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
